import { Search } from 'lucide-react'
import type React from 'react'
import DefinitionPopover from '@/components/glossary/DefinitionPopover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { GLOSSARY_TERMS } from '@/constants'
import { useLanguage } from '@/landing/contexts/LanguageContext'
import type { SearchBySectionModulusProps } from '@/types/components/landing'
import { PanelSuggestions } from './PanelSuggestions'

export const SearchBySectionModulus: React.FC<SearchBySectionModulusProps> = ({
  sectionModulus,
  sectionModulusUnit,
  showModulusSuggestions,
  modulusSuggestions,
  modulusSearchLoading,
  modulusSearchRef,
  onSectionModulusChange,
  onUnitChange,
  onShowSuggestionsChange,
  onSelectPanel,
  onSearch,
  onFocus,
}) => {
  const { t } = useLanguage()

  const unitDisplay = sectionModulusUnit === 'metric' ? 'cm³/m' : 'in³/ft'

  return (
    <div className="mb-12">
      <h2
        className="text-3xl font-bold text-gray-900 mb-2"
        style={{
          fontFamily: 'Montserrat, sans-serif',
        }}
      >
        {t('main_page.search.by_section_modulus.search_by')}{' '}
        <DefinitionPopover
          term={GLOSSARY_TERMS.ELASTIC_SECTION_MODULUS.handle}
          className="text-primary hover:text-primary"
          side="top"
          align="center"
        >
          {t(`glossary.${GLOSSARY_TERMS.ELASTIC_SECTION_MODULUS.handle}`)}
        </DefinitionPopover>
      </h2>
      <p className="text-gray-600 mb-6">{t('main_page.search.by_section_modulus.description')}</p>
      <div className="flex flex-col md:flex-row gap-4 md:items-center">
        <div className="order-1 md:order-2">
          <Select value={sectionModulusUnit} onValueChange={onUnitChange}>
            <SelectTrigger className="w-full md:w-48 py-6 text-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="metric">
                {t('main_page.search.by_section_modulus.units.metric')}
              </SelectItem>
              <SelectItem value="imperial">
                {t('main_page.search.by_section_modulus.units.imperial')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 relative order-2 md:order-1" ref={modulusSearchRef}>
          <Input
            type="number"
            value={sectionModulus}
            onChange={(e) => {
              onSectionModulusChange(e.target.value)
              onShowSuggestionsChange(e.target.value.length > 0)
            }}
            onFocus={onFocus}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSearch()
              }
            }}
            placeholder={sectionModulusUnit === 'metric' ? 'e.g., 6100' : 'e.g., 113.4'}
            className="text-lg py-6 px-4"
          />

          {showModulusSuggestions && (
            <PanelSuggestions
              isLoading={modulusSearchLoading}
              suggestions={modulusSuggestions}
              searchValue={sectionModulus}
              unitDisplay={unitDisplay}
              onSelectPanel={onSelectPanel}
              exactMatchHeaderKey="main_page.search.by_section_modulus.exact_match_header"
              exactMatchKey="main_page.search.by_section_modulus.exact_match"
              valueType="sectionModulus"
            />
          )}
        </div>
        <Button
          onClick={onSearch}
          disabled={!sectionModulus.trim()}
          size="lg"
          className="p-6 md:px-8 order-3"
        >
          <Search className="w-5 h-5 md:mr-2" />
          <span className="hidden md:inline">{t('main_page.search.by_name.button')}</span>
        </Button>
      </div>
    </div>
  )
}
